import {
  getWarningLevel,
  sortWarningsBySeverity,
} from '@src/shared/lib/warnings';
import type { AbsenceWarningSummary, AbsenceWarnings } from '../types';

export type GroupedResource = {
  team?: unknown;
  warningSummary?: AbsenceWarningSummary | null;
};

/**
 * One warning per group, standing for the warnings of all its rows.
 *
 * Each kind of warning is listed once however many rows carry it, red before
 * amber, and the group is red as soon as one of its rows is. Groups without a
 * warning are left out.
 */
export function summarizeGroupWarnings(
  resources: readonly GroupedResource[],
): AbsenceWarnings<string> {
  const warningsByGroup = new Map<
    string,
    Map<string, AbsenceWarningSummary['warnings'][number]>
  >();
  for (const resource of resources) {
    const warnings = resource.warningSummary?.warnings;
    if (!warnings?.length) continue;
    const groupId = String(resource.team ?? '');
    let unique = warningsByGroup.get(groupId);
    if (!unique) {
      unique = new Map();
      warningsByGroup.set(groupId, unique);
    }
    for (const warning of warnings) {
      if (!unique.has(warning.code)) unique.set(warning.code, warning);
    }
  }

  const summaries: AbsenceWarnings<string> = new Map();
  for (const [groupId, unique] of warningsByGroup) {
    const warnings = sortWarningsBySeverity([...unique.values()]);
    summaries.set(groupId, {
      level: getWarningLevel(warnings),
      count: warnings.length,
      warnings,
    });
  }
  return summaries;
}
